/**
 * Index de recherche globale contextuel.
 *
 * Agrège :
 * - profils (14)
 * - voies de classe (~73) + capacités (~360)
 * - voies de prestige (~60) + capacités
 * - peuples (8) + voies peuples
 * - créatures du bestiaire (~85)
 * - divinités (~32)
 * - termes du glossaire (~38)
 *
 * Le filtre est simple : match par sous-chaîne normalisée (lowercase + NFD).
 * Chaque entrée expose un `href` pour la navigation directe.
 */

import { loadGlossaire, loadPantheon } from '@data/content-loaders';
import { loadIndexes } from '@data/indexes';
import { formatModCaracEntry } from '@domain/peuple';
import { buildHref } from '../../router';

export type SearchResultKind =
  | 'profil'
  | 'voie'
  | 'capacite'
  | 'peuple'
  | 'creature'
  | 'divinite'
  | 'glossaire';

export interface SearchResult {
  readonly kind: SearchResultKind;
  readonly id: string;
  readonly title: string;
  readonly subtitle: string;
  readonly snippet: string;
  readonly href: string;
  /** Texte normalisé (minuscule, sans accents) pour le match rapide. */
  readonly _haystack: string;
}

let indexCache: readonly SearchResult[] | null = null;

export function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '');
}

function mk(result: Omit<SearchResult, '_haystack'>): SearchResult {
  return {
    ...result,
    _haystack: normalize(`${result.title} ${result.subtitle} ${result.snippet}`),
  };
}

/**
 * Construit l'index. Appel paresseux - la 1re recherche charge les ressources
 * et les met en cache pour toute la session.
 */
export async function buildSearchIndex(): Promise<readonly SearchResult[]> {
  if (indexCache) return indexCache;
  const [idx, glossaire, pantheon] = await Promise.all([
    loadIndexes(),
    loadGlossaire(),
    loadPantheon(),
  ]);

  const out: SearchResult[] = [];

  // Profils
  for (const p of idx.bdd.profils.profils) {
    out.push(
      mk({
        kind: 'profil',
        id: p.id,
        title: p.nom,
        subtitle: p.famille_id,
        snippet: `Caracs : ${p.caracteristiques_prioritaires.join(', ')}`,
        href: buildHref('profil', { id: p.id }),
      }),
    );
  }

  // Voies de classe + capacités
  for (const v of idx.bdd.voiesClasse.voies_classe) {
    const pid = v.profil_id ?? v.profils_ids?.[0];
    const profil = pid ? idx.profilById.get(pid) : null;
    const profilLabel =
      profil?.nom ?? (Array.isArray(v.profils_ids) ? v.profils_ids.join(', ') : '?');
    out.push(
      mk({
        kind: 'voie',
        id: v.id,
        title: v.nom,
        subtitle: `Voie - ${profilLabel}`,
        snippet: `${v.rangs.length} rangs`,
        href: pid ? buildHref('profil', { id: pid }) : '#/profils',
      }),
    );
    for (const r of v.rangs) {
      out.push(
        mk({
          kind: 'capacite',
          id: `${v.id}:${r.rang}`,
          title: r.nom,
          subtitle: `${v.nom} · rang ${r.rang}`,
          snippet: r.description.slice(0, 160),
          href: pid ? buildHref('profil', { id: pid }) : '#/profils',
        }),
      );
    }
  }

  // Voies de prestige + capacités
  for (const v of idx.bdd.voiesPrestige.voies_prestige) {
    out.push(
      mk({
        kind: 'voie',
        id: v.id,
        title: v.nom,
        subtitle: `Voie de prestige - ${v.categorie}`,
        snippet: `${v.rangs.length} rangs (${v.rangs[0]?.rang ?? 4}-${v.rangs.at(-1)?.rang ?? 8})`,
        href: buildHref('prestige'),
      }),
    );
    for (const r of v.rangs) {
      out.push(
        mk({
          kind: 'capacite',
          id: `${v.id}:${r.rang}`,
          title: r.nom,
          subtitle: `${v.nom} · rang ${r.rang}`,
          snippet: r.description.slice(0, 160),
          href: buildHref('prestige'),
        }),
      );
    }
  }

  // Peuples
  for (const p of idx.bdd.peuples.peuples) {
    out.push(
      mk({
        kind: 'peuple',
        id: p.id,
        title: p.nom,
        subtitle: 'Peuple',
        snippet: [...(p.modificateurs_carac.bonus ?? []), ...(p.modificateurs_carac.malus ?? [])]
          .map(formatModCaracEntry)
          .join(' · '),
        href: buildHref('peuple', { id: p.id }),
      }),
    );
  }

  // Voies de peuple
  for (const v of idx.bdd.peuples.voies_peuples) {
    out.push(
      mk({
        kind: 'voie',
        id: v.id,
        title: v.nom,
        subtitle: 'Voie de peuple',
        snippet: `${v.rangs.length} rangs`,
        href: v.peuple_id ? buildHref('peuple', { id: v.peuple_id }) : buildHref('peuples'),
      }),
    );
  }

  // Créatures
  for (const c of idx.creaturesAll) {
    out.push(
      mk({
        kind: 'creature',
        id: c.id,
        title: c.nom,
        subtitle: `Créature · NC ${c.nc ?? '-'}${c.taille ? ` · ${c.taille}` : ''}`,
        snippet:
          c.capacites
            ?.map((cap) => cap.nom)
            .join(', ')
            .slice(0, 160) ?? '',
        href: buildHref('creature', { id: c.id }),
      }),
    );
  }

  // Divinités
  for (const d of pantheon.divinites) {
    out.push(
      mk({
        kind: 'divinite',
        id: d.id,
        title: d.nom,
        subtitle: `Divinité - ${d.domaines.join(', ')}`,
        snippet: `${d.arme_sacree} · ${d.capacite_divine.label}`,
        href: buildHref('religions'),
      }),
    );
  }

  // Glossaire
  for (const e of glossaire.termes) {
    out.push(
      mk({
        kind: 'glossaire',
        id: e.terme,
        title: e.terme,
        subtitle: e.abreviations.length > 0 ? e.abreviations.join(', ') : e.categorie,
        snippet: e.definition.slice(0, 160),
        href: buildHref('glossaire'),
      }),
    );
  }

  indexCache = out;
  return out;
}

/**
 * Filtre l'index par query (substring normalisée), tronqué à `max` résultats.
 */
export function filterSearchIndex(
  index: readonly SearchResult[],
  query: string,
  max = 30,
): readonly SearchResult[] {
  const q = normalize(query.trim());
  if (!q) return [];
  const out: SearchResult[] = [];
  for (const r of index) {
    if (r._haystack.includes(q)) {
      out.push(r);
      if (out.length >= max) break;
    }
  }
  return out;
}

export function _resetSearchCache(): void {
  indexCache = null;
}
